import { AuthMethodTypeEnum, ObjectDirectiveEnum } from "@/api/common-types";
import iconCopyBlack from "@/assets/img/icon-copy-black.svg";
import iconCopyWhite from "@/assets/img/icon-copy-white.svg";
import editIconBlack from "@/assets/img/icon-edit-black.svg";
import editIconWhite from "@/assets/img/icon-edit-white.svg";
import iconSlidersBlack from "@/assets/img/icon-sliders-black.svg";
import iconSlidersWhite from "@/assets/img/icon-sliders-white.svg";
import trashIconRed from "@/assets/img/icon-trash-red.svg";
import iconLockBlack from "@/assets/img/locked-cell-black.svg";
import iconLockWhite from "@/assets/img/locked-cell-white.svg";
import UserPermissions from "@/mixins/UserPermissions.mixin";
import { IApiState, IObjectTypeState } from "@/models";
import _cloneDeep from "lodash/cloneDeep";
import ShortUniqueId from "short-unique-id";
import Component, { mixins } from "vue-class-component";
import { Emit, Prop } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import ApiTypesUpdater from "../object-type/ApiTypesUpdater.mixin";
import {
  Button,
  ButtonShapeEnum,
  ButtonSizeEnum,
  ButtonThemeEnum,
} from "../shared/Button.component";
import Dropdown from "../shared/Dropdown.component";
import ApiDataObjectTypeForm from "./ApiDataObjectTypeForm.component";
import styles from "./ApiDataObjectTypeOptions.component.module.scss";
import { ApiDataObjectTypePermissionsForm } from "./ApiDataObjectTypePermissionsForm.component";
import { ApiDataObjectTypeSettingsForm } from "./ApiDataObjectTypeSettingsForm.component";

const editIcon = editIconBlack;
const editIconHover = editIconWhite;
const copyIcon = iconCopyBlack;
const copyIconHover = iconCopyWhite;
const lockIcon = iconLockBlack;
const lockIconHover = iconLockWhite;
const settingsIcon = iconSlidersBlack;
const settingsIconHover = iconSlidersWhite;

@Component
export class ApiDataObjectTypeOptions extends mixins(
  ApiTypesUpdater,
  UserPermissions,
) {
  _tsx!: tsx.DeclareProps<{
    typeName: ApiDataObjectTypeOptions["typeName"];
    apiState?: ApiDataObjectTypeOptions["apiState"];
  }> &
    tsx.DeclareOnEvents<{
      onUpdateSelectedObjectType: IObjectTypeState;
    }>;
  $refs!: {
    container: HTMLDivElement;
    dropdown?: Dropdown;
  };

  @Prop({
    default: () => ({}),
  })
  apiState: IApiState;
  @Prop({ required: true }) private typeName: string;

  @Emit("updateSelectedObjectType") onUpdateSelectedObjectType(
    _objectType: IObjectTypeState,
  ) {}

  private isEditingType = false;
  private isEditingTypePermissions = false;
  private isEditingTypeSettings = false;

  private get objectType() {
    return this.apiState.types.find((type) => type.name === this.typeName);
  }

  private get isUsersTable() {
    return this.objectType?.directives?.includes(ObjectDirectiveEnum.USERS);
  }

  private get isPublishedUsersTable() {
    return this.isUsersTable && this.apiState.graphqlUrl;
  }

  private async deleteType() {
    if (
      window.confirm(
        this.$t("api_details_data_delete_type_confirm", {
          typeName: this.typeName,
        }).toString(),
      )
    ) {
      const newObjectTypes = this.apiState.types.filter(
        (type) => type.name !== this.typeName,
      );
      const newState = {
        ...this.apiState,
        types: newObjectTypes,
      };
      await this.updateApiDeclarationFromState(newState);
      if (this.serverSideErrors) {
        return;
      }
      this.closeDropdown();
      this.apiState.types = newState.types;
      const newSelectedObjectType =
        this.apiState.types[this.apiState.types.length - 1];
      if (newSelectedObjectType) {
        this.onUpdateSelectedObjectType(newSelectedObjectType);
      }
    } else {
      this.closeDropdown();
    }
  }

  private async cloneType() {
    if (!this.objectType) {
      return;
    }

    const latestCopyX = this.apiState.types.reduce((top, type) => {
      if (type.name.includes(`${this.objectType?.name}Copy`)) {
        const num = parseInt(
          type.name.replace(`${this.objectType?.name}Copy`, ""),
          10,
        );
        if (num > top) {
          return num;
        }
      }
      return top;
    }, 0);

    const newType = _cloneDeep(this.objectType);
    const { randomUUID } = new ShortUniqueId({ length: 6 });
    newType.id = `${randomUUID()}`;
    newType.name = `${newType.name}Copy${
      latestCopyX === 0 ? "" : latestCopyX + 1
    }`;
    newType.parents = { first: null, second: null };
    newType.targets = [];

    const oldTypeIndex = this.apiState.types.findIndex(
      (type) => type.name === this.typeName,
    );
    const newTypes = [
      ...this.apiState.types.slice(0, oldTypeIndex + 1),
      newType,
      ...this.apiState.types.slice(oldTypeIndex + 1),
    ];
    const newState = {
      ...this.apiState,
      types: newTypes,
    };
    await this.updateApiDeclarationFromState(newState);
    if (this.serverSideErrors) {
      return;
    }
    this.closeDropdown();
    this.apiState.types = newState.types;
    this.onUpdateSelectedObjectType(newType);
  }

  private closeDropdown() {
    this.$refs.dropdown?.close();
  }

  private editType() {
    this.isEditingType = true;
  }

  private editTypePermissions() {
    this.isEditingTypePermissions = true;
  }

  private editTypeSettings() {
    this.isEditingTypeSettings = true;
  }

  private onSubmitEdit(objectType: IObjectTypeState) {
    this.onUpdateSelectedObjectType(objectType);
    this.closeDropdown();
  }

  private onCancelEdit() {
    this.isEditingType = false;
  }

  private onCancelEditPermissions() {
    this.isEditingTypePermissions = false;
  }

  private onCancelEditSettings() {
    this.isEditingTypeSettings = false;
  }

  private get canEditPermissions() {
    return (
      [
        AuthMethodTypeEnum.AWS_OIDC,
        AuthMethodTypeEnum.AWS_COGNITO_USER_POOLS,
      ] as string[]
    ).includes(this.apiState.defaultAuthMode ?? "");
  }

  public render() {
    return this.isEditingType ? (
      <ApiDataObjectTypeForm
        apiState={this.apiState}
        typeName={this.typeName}
        onCancel={this.onCancelEdit}
        onSubmit={this.onSubmitEdit}
      />
    ) : this.isEditingTypePermissions ? (
      <ApiDataObjectTypePermissionsForm
        apiState={this.apiState}
        typeName={this.typeName}
        onCancel={this.onCancelEditPermissions}
        onSubmit={this.onCancelEditPermissions}
      />
    ) : this.isEditingTypeSettings ? (
      <ApiDataObjectTypeSettingsForm
        apiState={this.apiState}
        typeName={this.typeName}
        onCancel={this.onCancelEditSettings}
        onSubmit={this.onCancelEditSettings}
      />
    ) : (
      <div>
        {this.hasAdminPermissions && (
          <p class={styles.idField}>ID: {this.objectType?.id}</p>
        )}
        <Button
          class={styles.actionButton}
          shape={ButtonShapeEnum.square}
          theme={ButtonThemeEnum.naked}
          size={ButtonSizeEnum.sm}
          onClick={this.editType}
          disabled={this.isPublishedUsersTable}
          scopedSlots={{
            content: ({ isHovered }) => [
              <img
                src={isHovered ? editIconHover : editIcon}
                class={styles.icon}
              />,
              <span>{this.$t("api_details_data_edit_type")}</span>,
            ],
          }}
        />
        <Button
          class={styles.actionButton}
          shape={ButtonShapeEnum.square}
          theme={ButtonThemeEnum.naked}
          size={ButtonSizeEnum.sm}
          onClick={this.cloneType}
          disabled={this.isPublishedUsersTable}
          scopedSlots={{
            content: ({ isHovered }) => [
              <img
                src={isHovered ? copyIconHover : copyIcon}
                class={styles.icon}
              />,
              <span>{this.$t("api_details_data_duplicate_type")}</span>,
            ],
          }}
        />
        {this.canEditPermissions && <Dropdown.Divider />}
        {this.canEditPermissions && (
          <div>
            <Button
              class={styles.actionButton}
              shape={ButtonShapeEnum.square}
              theme={ButtonThemeEnum.naked}
              size={ButtonSizeEnum.sm}
              onClick={this.editTypePermissions}
              scopedSlots={{
                content: ({ isHovered }) => [
                  <img
                    src={isHovered ? lockIconHover : lockIcon}
                    class={styles.icon}
                  />,
                  <span>
                    {this.$t("api_details_data_edit_type_permissions")}
                  </span>,
                ],
              }}
            />
            <Button
              class={styles.actionButton}
              shape={ButtonShapeEnum.square}
              theme={ButtonThemeEnum.naked}
              size={ButtonSizeEnum.sm}
              onClick={this.editTypeSettings}
              disabled={this.isUsersTable}
              scopedSlots={{
                content: ({ isHovered }) => [
                  <img
                    src={isHovered ? settingsIconHover : settingsIcon}
                    class={styles.icon}
                  />,
                  <span>
                    {this.$t("api_details_data_edit_field_settings")}
                  </span>,
                ],
              }}
            />
          </div>
        )}
        <Dropdown.Divider />
        <Button
          class={styles.deleteButton}
          shape={ButtonShapeEnum.square}
          theme={ButtonThemeEnum.naked}
          size={ButtonSizeEnum.sm}
          onClick={this.deleteType}
          disabled={this.isPublishedUsersTable}
        >
          <img src={trashIconRed} class={styles.icon} />
          {this.$t("api_details_data_delete_type")}
        </Button>
      </div>
    );
  }
}
